import type {
  AmountAdjustment,
  CreditSettlement,
  Money,
  WithdrawalLineageNode,
} from '@pf/contracts';
import { Link, useParams } from '@tanstack/react-router';
import { FileSearch } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@/lib/api/http';
import { useOpsTransaction, useWithdrawalLineage } from '@/lib/api/ops-queries';
import { formatDateTime } from '@/lib/datetime';
import { compareDecimalStrings, differenceDecimalStrings, formatMoney } from '@/lib/money';
import { HISTORY_ORIGIN, transactionReturnHref } from '@/lib/nav/return-to';
import { shortId } from '@/lib/utils';
import { CheckMatrix } from '@/components/domain/check-matrix';
import { CopyButton } from '@/components/domain/copy-button';
import { CustomerUploadCard } from '@/components/domain/customer-upload-card';
import { MoneyDisplay } from '@/components/domain/money-display';
import { ParticipantPair } from '@/components/domain/participant-chip';
import { SlipPreviewButton } from '@/components/domain/slip-preview-dialog';
import { StateBadge } from '@/components/domain/state-badge';
import { StateTimeline } from '@/components/domain/state-timeline';
import { BackLink } from '@/components/layout/back-link';
import { PageHeader } from '@/components/layout/page';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Callout, ErrorState, Skeleton } from '@/components/ui/feedback';
import { Code } from '@/components/ui/primitives';

/**
 * One finished (or in-flight) deposit, read-only: the facts, the verification checks, the
 * customer's uploaded slip, and the event timeline. Reached from the History and Withdrawal
 * queue rows; deliberately decision-free — deciding happens on the review screens.
 *
 * Because it is reached from two different queues, "back" is whichever one sent the operator
 * here, carried in the URL — see `@/lib/nav/return-to`. History is the fallback: it is the screen
 * this route belongs to, and where a pasted link should land.
 */
export function TransactionDetailPage(): ReactNode {
  const { t } = useTranslation();
  const { transactionId } = useParams({ strict: false });
  const detail = useOpsTransaction(transactionId ?? '');

  if (detail.isLoading) {
    return (
      <div className="space-y-4" role="status" aria-label={t('opsHistory.detail.loading')}>
        <Skeleton className="h-8 w-72" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (detail.isError || !detail.data) {
    return (
      <ErrorState
        error={detail.error ?? new Error('not found')}
        onRetry={() => {
          void detail.refetch();
        }}
      />
    );
  }
  const txn = detail.data;

  /**
   * Who cancelled an EXPIRED deposit, or null when the timer simply ran out (or the row predates
   * the cause being recorded). Mirrors `cancelledBy` on the History list, deliberately — the two
   * screens must never disagree about what a row *was*.
   */
  const expiryCancelledBy: 'PLAYER' | 'OPERATOR' | null =
    txn.expired_cause === 'PARTICIPANT_RELEASE'
      ? 'PLAYER'
      : txn.expired_cause === 'OPERATOR_CANCELLED'
        ? 'OPERATOR'
        : null;

  const copyBeside = (value: string, label: string): ReactNode => (
    <CopyButton value={value} label={label} className="size-5 shrink-0 [&_svg]:size-3" />
  );

  /**
   * A customer's handle: the website's username when it has sent one, else the opaque
   * `account_ref`. Monospaced only for a raw ref — a username reads as a name, a ref is a token
   * whose characters get compared against a support ticket.
   */
  const handleValue = (
    display: string | null | undefined,
    accountRef: string | null | undefined,
    copyLabel: string,
  ): ReactNode => {
    const shown = display ?? accountRef;
    if (shown == null) return <span className="text-muted-foreground">—</span>;
    return (
      <span className="inline-flex max-w-full items-center gap-1">
        <span className={display != null ? 'truncate' : 'truncate font-mono text-xs'}>{shown}</span>
        {copyBeside(shown, copyLabel)}
      </span>
    );
  };

  const facts: { label: string; value: ReactNode }[] = [
    {
      label: t('opsHistory.detail.amount'),
      // `amount` is the *reserved* figure and stays that way even when the credit was
      // adjusted — so an adjusted deposit gets a chip pointing at the card that says what was
      // actually paid, rather than leaving this number to read as the amount that moved.
      value: (
        <span className="inline-flex flex-wrap items-baseline gap-2">
          <MoneyDisplay amount={txn.amount} size="lg" />
          {txn.verification?.adjustment ? (
            <span className="rounded-sm bg-amber-500/15 px-1.5 py-0.5 text-2xs font-medium text-amber-700 dark:text-amber-400">
              {t('opsHistory.detail.adjusted.chip', {
                amount: formatMoney(txn.verification.adjustment.paid_amount),
              })}
            </span>
          ) : null}
        </span>
      ),
    },
    {
      label: t('opsHistory.detail.depositor'),
      value:
        txn.depositor_name == null ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <span className="inline-flex max-w-full items-center gap-1">
            <span className="truncate">{txn.depositor_name}</span>
            {copyBeside(txn.depositor_name, t('opsHistory.copyLabels.depositorName'))}
          </span>
        ),
    },
    {
      label: t('opsHistory.detail.depositorUsername'),
      value: handleValue(
        txn.depositor_account_display,
        txn.depositor_account_ref,
        t('opsHistory.copyLabels.username'),
      ),
    },
    // Both sides' websites, together — since cross-website matching a deposit's own participant
    // no longer says who was paid. Sits between the depositor's facts and the withdrawer's, the
    // same place `ParticipantPair` reads best: after the depositor's identity is established,
    // before the withdrawer's begins.
    {
      label: t('opsHistory.detail.websites'),
      value: (
        <ParticipantPair depositor={txn.participant} withdrawer={txn.withdrawal_participant} />
      ),
    },
    {
      label: t('opsHistory.detail.withdrawer'),
      value:
        txn.withdrawer_name == null ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <span className="inline-flex max-w-full items-center gap-1">
            <span className="truncate">{txn.withdrawer_name}</span>
            {copyBeside(txn.withdrawer_name, t('opsHistory.copyLabels.withdrawerName'))}
          </span>
        ),
    },
    {
      label: t('opsHistory.detail.withdrawerUsername'),
      value: handleValue(
        txn.withdrawer_account_display,
        txn.withdrawer_account_ref,
        t('opsHistory.copyLabels.withdrawerUsername'),
      ),
    },
    {
      label: t('opsHistory.columns.operator'),
      value:
        txn.operator_name == null ? (
          <span className="text-muted-foreground">{t('opsHistory.automatic')}</span>
        ) : (
          <span className="inline-flex max-w-full items-center gap-1">
            <span className="truncate">{txn.operator_name}</span>
            {copyBeside(txn.operator_name, t('opsHistory.copyLabels.operatorName'))}
          </span>
        ),
    },
    {
      label: t('opsHistory.detail.clientReference'),
      value: (
        <span className="inline-flex max-w-full items-center gap-1">
          <Code>{txn.client_reference}</Code>
          {copyBeside(txn.client_reference, t('opsHistory.detail.clientReference'))}
        </span>
      ),
    },
    {
      label: t('opsHistory.detail.matchedWithdrawal'),
      value:
        txn.matched_withdrawal_id === null ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <span className="inline-flex max-w-full items-center gap-1">
            <span className="font-mono text-xs">{txn.matched_withdrawal_id}</span>
            {copyBeside(txn.matched_withdrawal_id, t('opsHistory.copyLabels.withdrawalId'))}
          </span>
        ),
    },
    {
      label: t('opsHistory.detail.evidenceAttempts'),
      value: <span className="tabular-nums">{txn.evidence_attempts}</span>,
    },
    {
      label: t('opsHistory.detail.createdAt'),
      value: <span className="tabular-nums text-xs">{formatDateTime(txn.created_at)}</span>,
    },
    {
      label: t('opsHistory.detail.updatedAt'),
      value: <span className="tabular-nums text-xs">{formatDateTime(txn.updated_at)}</span>,
    },
  ];

  return (
    <>
      <div className="mb-3">
        <BackLink fallback={HISTORY_ORIGIN} />
      </div>
      <PageHeader
        title={`${t('opsHistory.detail.title')} ${shortId(txn.id)}`}
        description={txn.id}
        badge={
          <span className="inline-flex items-center gap-2">
            {/* An EXPIRED deposit somebody cancelled renders as cancelled, matching the History
                list — see `cancelledBy`/`displayStatus` there and DEPOSIT_DISPLAY_STATE_META.
                `RESERVATION_TTL` is the only cause that keeps the plain expired badge. */}
            <StateBadge machine="deposit" status={expiryCancelledBy === null ? txn.status : 'CANCELLED'} />
            {expiryCancelledBy !== null ? (
              <span className="text-2xs text-muted-foreground">
                {t(`states.cancelledBy.${expiryCancelledBy}`)}
              </span>
            ) : null}
          </span>
        }
      />

      {/* Screen-reader-only bridge: `PageHeader` renders the page's one `<h1>`, and every card
          below is a `<CardTitle>` (`<h3>`), so without this the heading levels jump straight from
          1 to 3 — this is the only screen in the operator console that puts a `Card` directly
          under a `PageHeader` with no `Section` (`<h2>`) between them. A visible `Section` title
          would duplicate what `PageHeader` already says; this keeps the heading order valid for
          screen-reader navigation without changing how the page looks. */}
      <h2 className="sr-only">{t('opsHistory.detail.sectionsHeading')}</h2>
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('opsHistory.detail.factsTitle')}</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
                {facts.map(({ label, value }) => (
                  <div key={label} className="space-y-0.5">
                    <dt className="text-2xs text-muted-foreground">{label}</dt>
                    <dd className="text-sm">{value}</dd>
                  </div>
                ))}
              </dl>
            </CardContent>
          </Card>

          {txn.verification?.adjustment ? (
            <AdjustedAmountCard
              adjustment={txn.verification.adjustment}
              settlement={txn.settlement}
            />
          ) : null}

          {txn.verification ? (
            <Card>
              <CardHeader>
                <CardTitle>{t('opsHistory.detail.checksTitle')}</CardTitle>
              </CardHeader>
              <CardContent>
                <CheckMatrix checks={txn.verification.checks} />
              </CardContent>
            </Card>
          ) : null}

          {txn.matched_withdrawal_id !== null ? (
            <SplitLineageCard withdrawalId={txn.matched_withdrawal_id} transactionId={txn.id} />
          ) : null}

          <CustomerUploadCard transactionId={txn.id} />
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t('opsHistory.detail.timelineTitle')}</CardTitle>
            </CardHeader>
            <CardContent>
              <StateTimeline events={txn.events} dense />
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}

/**
 * What the adjusted credit ("เกลี่ยยอด") actually did — the card that answers "how was this
 * approved when the slip says 60 and the reservation says 100?".
 *
 * Two halves, deliberately separate, because they can disagree and the disagreement is the
 * interesting part:
 *
 *  - **Instructed** (`adjustment`, off `slip.verified`): what the decision matrix worked out —
 *    reserved, paid, and the delta between them. Always present when this card renders.
 *  - **Settled** (`settlement`, off `transaction.completed`): what the website's ledger actually
 *    moved. Absent until the deposit completes, and *authoritative* when present: for an
 *    overpayment only the website knows how much of the extra the withdrawer's balance could
 *    cover, so `extra_debited_amount` is capped at their available balance and may be less than
 *    the delta the matrix instructed.
 *
 * That cap is why the shortfall line exists. When it bites, the depositor is credited less than
 * they transferred and nothing else on this screen says so — the transaction is COMPLETED, no
 * review case was raised, and `amount` still shows the reserved figure. Rendering the gap is not
 * a judgement about whether the cap is right; it is the difference being visible at all.
 */
function AdjustedAmountCard({
  adjustment,
  settlement,
}: {
  adjustment: AmountAdjustment;
  settlement: CreditSettlement | undefined;
}): ReactNode {
  const { t } = useTranslation();
  const under = adjustment.direction === 'UNDER';

  // Only meaningful for an overpayment: UNDER credits the paid amount exactly, by construction.
  const shortfall =
    settlement && !under && compareDecimalStrings(adjustment.paid_amount.value, settlement.credited_amount.value) > 0
      ? {
          value: differenceDecimalStrings(
            adjustment.paid_amount.value,
            settlement.credited_amount.value,
          ),
          currency: adjustment.paid_amount.currency,
        }
      : null;

  const rows: { label: string; value: ReactNode; strong?: boolean }[] = [
    {
      label: t('opsHistory.detail.adjusted.reserved'),
      value: <MoneyDisplay amount={adjustment.reserved_amount} />,
    },
    {
      label: t('opsHistory.detail.adjusted.paid'),
      value: <MoneyDisplay amount={adjustment.paid_amount} />,
    },
    {
      label: t(under ? 'opsHistory.detail.adjusted.short' : 'opsHistory.detail.adjusted.over'),
      value: <MoneyDisplay amount={adjustment.delta} />,
    },
  ];

  if (settlement) {
    rows.push({
      label: t('opsHistory.detail.adjusted.credited'),
      value: <MoneyDisplay amount={settlement.credited_amount} size="lg" />,
      strong: true,
    });
    if (settlement.released_amount) {
      rows.push({
        label: t('opsHistory.detail.adjusted.released'),
        value: <MoneyDisplay amount={settlement.released_amount} />,
        strong: true,
      });
    }
    if (settlement.extra_debited_amount) {
      rows.push({
        label: t('opsHistory.detail.adjusted.extraDebited'),
        value: <MoneyDisplay amount={settlement.extra_debited_amount} />,
        strong: true,
      });
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {t(
            under
              ? 'opsHistory.detail.adjusted.titleUnder'
              : 'opsHistory.detail.adjusted.titleOver',
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
          {rows.map(({ label, value, strong }) => (
            <div key={label} className="space-y-0.5">
              <dt className="text-2xs text-muted-foreground">{label}</dt>
              <dd className={strong === true ? 'text-sm font-medium' : 'text-sm'}>{value}</dd>
            </div>
          ))}
        </dl>
        {settlement ? null : (
          <p className="text-xs text-muted-foreground">
            {t('opsHistory.detail.adjusted.pending')}
          </p>
        )}
        {shortfall ? (
          <Callout tone="warning">
            {t('opsHistory.detail.adjusted.shortfall', { amount: formatMoney(shortfall) })}
          </Callout>
        ) : null}
      </CardContent>
    </Card>
  );
}

/**
 * The split-fill lineage card (migration 0034 / shrink-the-parent): how many slices were cut from
 * the withdrawal this deposit matched, which deposits filled them, how many succeeded, and
 * whether a funds lock went back to the withdrawer. `withdrawalId` is `matched_withdrawal_id`
 * straight off the transaction — it may name the parent or a slice, and the server resolves to
 * the root either way.
 *
 * Self-fetching, with its own loading/error/empty guard clauses, so a failure here can never blank
 * the Facts card above it. A 404 is the ordinary answer for the overwhelming majority of
 * withdrawals — the ones never split — so it renders nothing rather than an `ErrorState`; any
 * other failure gets the page's usual error box, because that is a real thing gone wrong.
 *
 * `transactionId` is the deposit whose page this is. The card is the only place the sibling
 * deposits — the other slices' — are named at all, so it carries the return address that lets an
 * operator go and read one and come back here (see `return-to.ts`), and marks the row that is
 * this page rather than linking it to itself.
 */
function SplitLineageCard({
  withdrawalId,
  transactionId,
}: {
  readonly withdrawalId: string;
  readonly transactionId: string;
}): ReactNode {
  const { t } = useTranslation();
  const lineage = useWithdrawalLineage(withdrawalId);

  if (lineage.isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('opsHistory.detail.lineage.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-40 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (lineage.isError) {
    // A withdrawal that was never split is the common case, not a failure — see the module docs.
    if (lineage.error instanceof ApiError && lineage.error.status === 404) return null;
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('opsHistory.detail.lineage.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <ErrorState
            error={lineage.error}
            onRetry={() => {
              void lineage.refetch();
            }}
          />
        </CardContent>
      </Card>
    );
  }

  if (!lineage.data) return null;
  const { origin, slices, totals, generated_at } = lineage.data;
  const cuts = totals.reconciles ? runningRemainders(origin.origin_amount, slices) : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('opsHistory.detail.lineage.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* The split UPDATE is the only statement allowed to write this row's amount — a mismatch
            here is a data-integrity alarm, not a rounding nit. */}
        {!totals.reconciles ? (
          <Callout tone="danger" title={t('opsHistory.detail.lineage.sumAlarm.title')}>
            {t('opsHistory.detail.lineage.sumAlarm.body')}
          </Callout>
        ) : null}

        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <span>
            {t('opsHistory.detail.lineage.totals.slicesCut', { count: totals.slices_total })}
          </span>
          <span aria-hidden="true">·</span>
          <span>{t('opsHistory.detail.lineage.totals.fulfilled', { count: totals.fulfilled })}</span>
          <span aria-hidden="true">·</span>
          <span>{t('opsHistory.detail.lineage.totals.inFlight', { count: totals.in_flight })}</span>
          <span aria-hidden="true">·</span>
          <span>{t('opsHistory.detail.lineage.totals.released', { count: totals.released })}</span>
          <span aria-hidden="true">·</span>
          <span>
            {t('opsHistory.detail.lineage.totals.repoolEvents', { count: totals.repool_events })}
          </span>
        </div>

        <ol className="space-y-3">
          <LineageNodeView
            node={origin}
            kind="origin"
            sliceCount={slices.length}
            transactionId={transactionId}
          />
          {slices.length > 0 ? (
            <li>
              <ol className="ml-3 space-y-3 border-l border-border pl-4">
                {slices.map((slice, index) => (
                  <LineageNodeView
                    key={slice.id}
                    node={slice}
                    kind="slice"
                    sliceCount={slices.length}
                    transactionId={transactionId}
                    cut={cuts?.[index]}
                  />
                ))}
              </ol>
            </li>
          ) : null}
        </ol>

        <p className="text-2xs text-muted-foreground">
          {t('opsHistory.detail.lineage.generatedAt', { at: formatDateTime(generated_at) })}
        </p>
      </CardContent>
    </Card>
  );
}

/** What the parent was worth either side of one cut. */
interface SliceCut {
  /** The parent's amount the instant before this slice was cut off it. */
  readonly before: Money;
  /** What it was left worth — the parent's amount until the next cut, or its residual. */
  readonly after: Money;
}

/**
 * What the parent was worth before and after each cut, in cut order.
 *
 * The intermediate amounts are recovered rather than read: the split UPDATE writes
 * `amount = amount - slice` in place and emits no event of its own, so a parent that went
 * 500 → 400 → 300 → 200 leaves 400 and 300 recorded nowhere at all. They are exactly derivable
 * from what is already on this screen, because `slices` arrives ordered by `split_sequence` —
 * which *is* the cut order, the sequence being `split_count + 1` at the moment of the cut — and
 * `origin_amount = residual + Σ slices` holds.
 *
 * Derived here rather than added to the wire for that reason: it is arithmetic over facts the
 * response already carries, not a new fact. Callers must skip it when `totals.reconciles` is
 * false — a chain walked over numbers that do not add up is a fabricated story, and the alarm
 * that renders above already says this lineage is not to be trusted.
 *
 * `differenceDecimalStrings` returns an absolute difference, which is safe only under that same
 * guard: with the invariant intact each `before` is at least its slice, so no subtraction here
 * can go negative and silently come back positive.
 */
function runningRemainders(
  originAmount: Money,
  slices: readonly WithdrawalLineageNode[],
): readonly SliceCut[] {
  let before = originAmount;
  return slices.map((slice) => {
    const after: Money = {
      value: differenceDecimalStrings(before.value, slice.amount.value),
      currency: before.currency,
    };
    const cut: SliceCut = { before, after };
    before = after;
    return cut;
  });
}

/** Props for {@link LineageNodeView}. */
interface LineageNodeViewProps {
  node: WithdrawalLineageNode;
  kind: 'origin' | 'slice';
  /** How many slices exist in total, for the "Slice N of total" heading. */
  sliceCount: number;
  /** The deposit being read — the attempt row that names it is "this one", not a link. */
  transactionId: string;
  /**
   * What the parent was worth either side of this cut. Absent on the parent, and absent on every
   * slice when the lineage does not reconcile — see {@link runningRemainders}.
   */
  cut?: SliceCut | undefined;
}

/**
 * One row of the lineage tree — the shrunk parent, or one slice cut from it.
 *
 * Parent and slice render identically (see `WithdrawalLineageNodeSchema`'s doc comment): the
 * parent is not an empty container that was replaced by its children, it is still the original
 * request, now worth the remainder — shown the same struck-through-origin-then-residual way the
 * withdrawal queue already renders a shrunk parent.
 *
 * `status` is never presented as the outcome on its own: `repools` renders unconditionally
 * whenever it is non-empty, regardless of what the row's current status reads as, because a
 * rejected deposit returns a slice to `AVAILABLE` — identical by status to one never touched.
 */
function LineageNodeView({
  node,
  kind,
  sliceCount,
  transactionId,
  cut,
}: LineageNodeViewProps): ReactNode {
  const { t } = useTranslation();
  const isShrunkParent = kind === 'origin' && node.origin_amount.value !== node.amount.value;

  return (
    <li className="space-y-2 rounded-md border border-border/60 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-2xs text-muted-foreground">{shortId(node.id)}</span>
        <StateBadge machine="withdrawal" status={node.status} size="sm" />
        {node.cancelled_by !== null ? (
          // Same fold as the withdrawal queue: WITHDRAWER reads as "by customer", matching the
          // `states.cancelledBy` vocabulary the rest of the console already uses.
          <span className="text-2xs text-muted-foreground">
            {t(
              node.cancelled_by === 'OPERATOR'
                ? 'states.cancelledBy.OPERATOR'
                : 'states.cancelledBy.PLAYER',
            )}
          </span>
        ) : null}
      </div>

      <p className="text-xs font-medium">
        {kind === 'origin'
          ? t('opsHistory.detail.lineage.originLabel')
          : t('opsHistory.detail.lineage.sliceHeading', {
              sequence: node.split_sequence ?? '?',
              total: sliceCount,
            })}
      </p>

      {isShrunkParent ? (
        <div className="flex items-center gap-1.5">
          <MoneyDisplay
            amount={node.origin_amount}
            size="sm"
            className="text-muted-foreground line-through"
          />
          <span aria-hidden="true" className="text-2xs text-muted-foreground">
            →
          </span>
          <MoneyDisplay amount={node.amount} />
        </div>
      ) : (
        <MoneyDisplay amount={node.amount} />
      )}

      {/* The parent's balance either side of this cut. Its own card can only ever show the two
          ends of the chain (`origin_amount → amount`), because the steps between them were
          overwritten in place; this is where they come back. */}
      {cut ? (
        <p className="text-2xs text-muted-foreground">
          {t('opsHistory.detail.lineage.cutFrom', {
            // Symboled, unlike the shortfall callout: this line sits directly under a
            // `MoneyDisplay`, and two amounts a centimetre apart must not be spelled differently.
            from: formatMoney(cut.before, { withSymbol: true }),
            remaining: formatMoney(cut.after, { withSymbol: true }),
          })}
        </p>
      ) : null}

      <p className="flex flex-wrap items-center gap-1.5 text-2xs text-muted-foreground">
        <span>{t('opsHistory.detail.lineage.lockReferenceLabel')}</span>
        <Code>{node.lock_reference}</Code>
      </p>

      {node.repools.length > 0 ? (
        <Callout
          tone="warning"
          title={t('opsHistory.detail.lineage.repoolHistoryTitle', {
            count: node.repools.length,
          })}
        >
          <ul className="space-y-1">
            {node.repools.map((repool, index) => (
              <li key={index} className="flex flex-wrap items-center gap-1.5">
                <Code>{repool.cause}</Code>
                <span className="text-2xs text-muted-foreground">
                  {formatDateTime(repool.at)}
                </span>
              </li>
            ))}
          </ul>
        </Callout>
      ) : null}

      {node.released !== null ? (
        // Never "refunded": PaymentFlow does not hold the withdrawer's balance, so this states
        // only the fact PaymentFlow can vouch for — the lock was released, at this instant, for
        // this cause.
        <p className="flex flex-wrap items-center gap-1.5 text-xs">
          <span className="font-medium">{t('opsHistory.detail.lineage.releasedTitle')}</span>
          <Code>{node.released.cause}</Code>
          <span className="text-2xs text-muted-foreground">
            {t('opsHistory.detail.lineage.releasedAt', {
              at: formatDateTime(node.released.at),
            })}
          </span>
        </p>
      ) : null}

      <div className="space-y-1">
        <p className="text-2xs font-medium text-muted-foreground">
          {t('opsHistory.detail.lineage.attemptsTitle')}
        </p>
        {node.attempts.length === 0 ? (
          <p className="text-2xs text-muted-foreground">
            {t('opsHistory.detail.lineage.noAttempts')}
          </p>
        ) : (
          <ul className="space-y-1.5">
            {node.attempts.map((attempt) => {
              // The deposit whose page this is appears in its own lineage. Linking it to itself
              // reads as another amount to go and check, so it is named and marked instead.
              const isCurrent = attempt.transaction_id === transactionId;
              return (
                <li
                  key={attempt.transaction_id}
                  className="flex flex-wrap items-center gap-1.5 text-2xs"
                >
                  {isCurrent ? (
                    <span className="font-mono font-medium text-foreground">
                      {shortId(attempt.transaction_id)}
                    </span>
                  ) : (
                    <Link
                      to="/ops/history/$transactionId"
                      params={{ transactionId: attempt.transaction_id }}
                      // The return address, so the sibling's back link comes back here rather
                      // than dropping the operator at the top of History — see `return-to.ts`.
                      search={{ from: transactionReturnHref(transactionId) }}
                      className="font-mono text-foreground underline-offset-2 hover:underline"
                    >
                      {shortId(attempt.transaction_id)}
                    </Link>
                  )}
                  {isCurrent ? (
                    <span className="rounded-sm bg-muted px-1 py-0.5 text-muted-foreground">
                      {t('opsHistory.detail.lineage.thisTransaction')}
                    </span>
                  ) : null}
                  <StateBadge machine="deposit" status={attempt.status} size="sm" />
                  <MoneyDisplay amount={attempt.amount} size="sm" />
                  {attempt.trans_ref !== null ? (
                    <Code>{attempt.trans_ref}</Code>
                  ) : (
                    <span className="text-muted-foreground">
                      {t('opsHistory.detail.lineage.noSlip')}
                    </span>
                  )}
                  <span className="text-muted-foreground">
                    {attempt.verified_at !== null
                      ? formatDateTime(attempt.verified_at)
                      : t('opsHistory.detail.lineage.notVerified')}
                  </span>
                  {/* Offered wherever a file was actually stored, including on this page's own
                      deposit and on attempts that never verified — the slip behind a failed
                      attempt is the one an operator most often wants to look at. */}
                  {attempt.has_slip_file ? (
                    <SlipPreviewButton transactionId={attempt.transaction_id} />
                  ) : null}
                  {/* Same button clothing as ดูสลิป beside it — as a bare underlined
                      word it read as body text and operators missed it. */}
                  {attempt.review_case_id !== null ? (
                    <Button
                      asChild
                      variant="outline"
                      size="sm"
                      className="h-6 gap-1 px-2 text-2xs [&_svg]:size-3"
                    >
                      <Link
                        to="/ops/reviews/$caseId"
                        params={{ caseId: attempt.review_case_id }}
                      >
                        <FileSearch aria-hidden="true" />
                        {t('opsHistory.detail.lineage.reviewCaseLink')}
                      </Link>
                    </Button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </li>
  );
}
